import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Grid,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  alpha,
  Skeleton,
} from '@mui/material';
import {
  TableChart as TableChartIcon,
  Co2,
  ShowChart,
  Public,
  FlightTakeoff,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { useFilters } from '../../contexts/FiltersContext';
import { useAnalyticsData } from '../../contexts/AnalyticsDataContext';
import { dashboardApi } from '../../services/dashboardApi';
import { useSplitView } from '../../contexts/SplitViewContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import ProfessionalKPICard from '../../components/common/ProfessionalKPICard';
import { formatTransportMode } from '../../utils/transportModeDisplay';
import { alpha3ToAlpha2Code } from '../../utils/countryCodeMapping';
import { formatNumber, formatStringValue } from '../../utils/numberFormat';

interface KPICardData {
  label: string;
  subLabel: string;
  value: string | number;
  unit: string;
}

interface TabInfo {
  id: string;
  name: string;
  description: string;
}

interface EmissionRow {
  dimension: string;
  total_emissions: number;
  transported_weight: number;
  distance: number;
  emission_intensity: number;
  count: number;
  compensated_emissions: number;
  pending_emissions: number;
  not_compensated_emissions: number;
}

interface TabData {
  data: EmissionRow[];
  metadata: {
    total: EmissionRow;
    totalRows?: number;
    total_rows?: number;
  };
}

const PAGINATED_TAB_IDS = ['customer', 'origin_city', 'destination_city'] as const;
const TAB_PAGE_SIZE = 10;
const VALID_TIME_PERIODS = new Set(['YTD', 'MTD', 'L12M', 'HISTORIC']);

// Applies a tab id (from URL) and returns it as the default, or null when absent.
const applyUrlTabId = (
  urlTab: string | null,
  setActiveTabId: (id: string) => void,
): string | null => {
  if (urlTab) {
    setActiveTabId(urlTab);
    return urlTab;
  }
  return null;
};

// Applies a time-period type if it is one of the valid periods.
const applyTimeType = (
  type: string | null,
  changeTimeFilter: (arg: { key: any; value: string }) => void,
): void => {
  if (type && VALID_TIME_PERIODS.has(type)) {
    changeTimeFilter({ key: type as any, value: type });
  }
};

// Resolves the default tab id from DB defaults / URL params and applies the
// matching time filter. Tab priority: URL > DB; type priority: URL > DB.
const resolveDefaultTabId = async (
  urlTab: string | null,
  urlType: string | null,
  setActiveTabId: (id: string) => void,
  changeTimeFilter: (arg: { key: any; value: string }) => void,
): Promise<string | null> => {
  try {
    const defData = await dashboardApi.getDefaultFilter('emission_dashboard');
    if (defData.code === 200 && defData.response) {
      const { analytics_tab_id, time_period } = defData.response;

      // Tab Priority: URL > DB > Default
      let defaultTabId = applyUrlTabId(urlTab, setActiveTabId);
      if (!defaultTabId && analytics_tab_id) {
        defaultTabId = analytics_tab_id;
        setActiveTabId(analytics_tab_id);
      }

      // Type Priority: URL > DB > Default
      applyTimeType(urlType || time_period, changeTimeFilter);
      return defaultTabId;
    }
    // If no DB defaults, still check URL
    const defaultTabId = applyUrlTabId(urlTab, setActiveTabId);
    applyTimeType(urlType, changeTimeFilter);
    return defaultTabId;
  } catch (error) {
    console.error('Failed to fetch default filter:', error);
    // On error, still apply URL params if they exist
    const defaultTabId = applyUrlTabId(urlTab, setActiveTabId);
    applyTimeType(urlType, changeTimeFilter);
    return defaultTabId;
  }
};

interface SubHeaderData {
  transportModes: Array<{ label: string; value: string }>;
  topOriginCountries: Array<{ label: string; value: string }>;
  topDestinationCountries: Array<{ label: string; value: string }>;
}

interface EmissionChartData {
  primarySubHeader: { name: string; value: string };
  secondarySubHeader: { name: string; value: number };
  chartData: {
    type: string;
    name: string;
    keyConfig: string[];
    data: Array<{ name: string; value: number }>;
  };
}

// Professional color palette
const CHART_COLORS = {
  primary: '#90BF45',      // CarbonCare green
  secondary: '#6C757D',    // Gray
  success: '#28a745',
  info: '#17a2b8',
  warning: '#ffc107',
  danger: '#dc3545',
};

// KPI loading skeleton card. A verbatim-identical copy of this block also
// lives in Analytics (exported as KpiSkeletonCard); it is kept local here
// because importing the Analytics module would pull react-simple-maps into
// the Dashboard test bundle, which jest cannot transform.
const KpiSkeletonCard: React.FC<{ idx: number; isSplitView: boolean }> = ({ idx, isSplitView }) => (
  <Box key={`kpi-skeleton-${idx}`} sx={{ minWidth: isSplitView ? 180 : 'auto', flex: isSplitView ? '1 1 250px' : '1 1 auto' }}>
    <Paper sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Skeleton variant="circular" width={48} height={48} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="70%" sx={{ fontSize: '0.75rem' }} />
        <Skeleton variant="text" width="55%" sx={{ fontSize: '1.5rem' }} />
        <Skeleton variant="text" width="40%" sx={{ fontSize: '0.7rem' }} />
      </Box>
    </Paper>
  </Box>
);

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const filters = useFilters();
  const { activeTabId, setActiveTabId } = useAnalyticsData();
  const { isSplitView } = useSplitView();
  const location = useLocation();

  // Extract URL params at component level for report mode guard
  const searchParams = new URLSearchParams(location.search);
  const urlLicensee = searchParams.get('licensee');
  const isReportMode = searchParams.get('report_mode') === 'true';

  // State
  const [kpiData, setKpiData] = useState<KPICardData[]>([]);
  const [subHeaderData, setSubHeaderData] = useState<SubHeaderData | null>(null);
  const [emissionChartData, setEmissionChartData] = useState<EmissionChartData | null>(null);
  const [tabs, setTabs] = useState<TabInfo[]>([]);
  const [tabData, setTabData] = useState<TabData | null>(null);
  const [tabPage, setTabPage] = useState(0);

  // Selected tab based on shared state
  const selectedTab = tabs.find(t => t.id === activeTabId) || (tabs.length > 0 ? tabs[0] : null);
  console.log('SELECTED TAB ', selectedTab?.name)
  const isPaginatedTab = !!selectedTab && (PAGINATED_TAB_IDS as readonly string[]).includes(selectedTab.id);

  // Loading states
  const [kpiLoading, setKpiLoading] = useState(true);
  const [subHeaderLoading, setSubHeaderLoading] = useState(true);
  const [emissionChartLoading, setEmissionChartLoading] = useState(true);
  const [tabDataLoading, setTabDataLoading] = useState(true);

  // Unit selection
  const [selectedEmission, setSelectedEmission] = useState('TOT');
  const [selectedWeight, setSelectedWeight] = useState('kg');

  // Fetch KPI data with debounce
  useEffect(() => {
    // In report mode, wait for the licensee filter to be applied before fetching
    if (isReportMode && urlLicensee && filters.licenseeData.length === 0) {
      return;
    }
    const timeoutId = setTimeout(() => {
      const fetchKPIData = async () => {
        setKpiLoading(true);
        try {
          const payload = filters.buildFilterPayload();
          const response = await dashboardApi.getHeaderData(payload);
          if (response.code === 200) {
            setKpiData(response.response);
          }
        } catch (error) {
          console.error('Failed to fetch KPI data:', error);
        } finally {
          setKpiLoading(false);
        }
      };

      fetchKPIData();
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [
    filters.licenseeData,
    filters.subCustomerData,
    filters.subCompanyData,
    filters.branchData,
    filters.transportModeData,
    filters.countryOriginData,
    filters.countryDestinationData,
    filters.cityOriginData,
    filters.cityDestinationData,
    filters.fromToDate,
    filters.timeFilter,
  ]);

  // Fetch Sub Header data with debounce
  useEffect(() => {
    // In report mode, wait for the licensee filter to be applied before fetching
    if (isReportMode && urlLicensee && filters.licenseeData.length === 0) {
      return;
    }
    const timeoutId = setTimeout(() => {
      const fetchSubHeaderData = async () => {
        setSubHeaderLoading(true);
        try {
          const payload = filters.buildFilterPayload();
          const response = await dashboardApi.getSubHeaderData(payload);
          if (response.code === 200) {
            setSubHeaderData(response.response);
          }
        } catch (error) {
          console.error('Failed to fetch sub header data:', error);
        } finally {
          setSubHeaderLoading(false);
        }
      };

      fetchSubHeaderData();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    filters.licenseeData,
    filters.subCustomerData,
    filters.subCompanyData,
    filters.branchData,
    filters.transportModeData,
    filters.countryOriginData,
    filters.countryDestinationData,
    filters.cityOriginData,
    filters.cityDestinationData,
    filters.fromToDate,
    filters.timeFilter,
  ]);

  // Fetch Emission Chart data with debounce
  useEffect(() => {
    if (isReportMode && urlLicensee && filters.licenseeData.length === 0) {
      return;
    }
    const timeoutId = setTimeout(() => {
      const fetchEmissionChartData = async () => {
        setEmissionChartLoading(true);
        try {
          const payload = filters.buildFilterPayload();
          const response = await dashboardApi.getEmissionChartData(payload);
          if (response.code === 200) {
            setEmissionChartData(response.response);
          }
        } catch (error) {
          console.error('Failed to fetch emission chart data:', error);
        } finally {
          setEmissionChartLoading(false);
        }
      };

      fetchEmissionChartData();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    filters.licenseeData,
    filters.subCustomerData,
    filters.subCompanyData,
    filters.branchData,
    filters.transportModeData,
    filters.countryOriginData,
    filters.countryDestinationData,
    filters.cityOriginData,
    filters.cityDestinationData,
    filters.fromToDate,
    filters.timeFilter,
  ]);

  // Fetch tab info and apply saved default filter
  useEffect(() => {
    const init = async () => {
      const urlTab = searchParams.get('tab');
      const urlType = searchParams.get('type');

      const defaultTabId = await resolveDefaultTabId(
        urlTab,
        urlType,
        setActiveTabId,
        filters.changeTimeFilter,
      );

      // 2. Licensee override from URL
      if (urlLicensee) {
        filters.setLicenseeData([{
          id: urlLicensee,
          key: 'licensee',
          type: 'licensee',
          value: `Licensee ID: ${urlLicensee}`
        }]);
      }

      try {
        const response = await dashboardApi.getTabInfo();
        if (response.code === 200) {
          setTabs(response.response);
          if (response.response.length > 0) {
            if (defaultTabId) {
              setActiveTabId(defaultTabId);
            } else if (!activeTabId) {
              setActiveTabId(response.response[0].id);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch tab info:', error);
      }
    };

    init();
  }, []);

  // Fetch tab data with debounce
  useEffect(() => {
    // In report mode, wait for the licensee filter to be applied before fetching
    if (isReportMode && urlLicensee && filters.licenseeData.length === 0) {
      return;
    }
    if (!selectedTab) return;

    const timeoutId = setTimeout(() => {
      const fetchTabData = async () => {
        setTabDataLoading(true);
        try {
          const payload = filters.buildFilterPayload({
            tab_type: selectedTab.id,
            co2Type: selectedEmission,
            ...(isPaginatedTab && {
              page: tabPage + 1,
              limit: TAB_PAGE_SIZE,
            }),
          });
          const response = await dashboardApi.getTabData(payload);
          if (response.code === 200) {
            setTabData(response.response);
          }
        } catch (error) {
          console.error('Failed to fetch tab data:', error);
        } finally {
          setTabDataLoading(false);
        }
      };

      fetchTabData();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    selectedTab?.id,
    selectedEmission,
    filters.licenseeData,
    filters.subCustomerData,
    filters.subCompanyData,
    filters.branchData,
    filters.transportModeData,
    filters.countryOriginData,
    filters.countryDestinationData,
    filters.cityOriginData,
    filters.cityDestinationData,
    filters.fromToDate,
    filters.timeFilter,
    tabPage,
    isPaginatedTab,
  ]);

  // Reset to page 0 when tab or filters change so a new dataset starts at the first page
  useEffect(() => {
    setTabPage(0);
  }, [
    selectedTab?.id,
    selectedEmission,
    filters.licenseeData,
    filters.subCustomerData,
    filters.subCompanyData,
    filters.branchData,
    filters.transportModeData,
    filters.countryOriginData,
    filters.countryDestinationData,
    filters.cityOriginData,
    filters.cityDestinationData,
    filters.fromToDate,
    filters.timeFilter,
  ]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (tabs[newValue]) {
      setActiveTabId(tabs[newValue].id);
    }
  };

  const convertWeight = (value: number): string => {
    let converted: number;
    switch (selectedWeight) {
      case 'ton':
        converted = value / 1000;
        break;
      case 'lb':
        converted = value * 2.20462;
        break;
      default:
        converted = value;
    }
    return formatNumber(converted, 2);
  };

  const getWeightUnit = () => {
    switch (selectedWeight) {
      case 'ton':
        return 'ton';
      case 'lb':
        return 'lb';
      default:
        return 'kg';
    }
  };

  const convertSubCustomerName = (name: string | undefined) => {
    const normalizedName = name?.trim();

    if (!normalizedName || normalizedName.toLowerCase() === 'main') {
      return 'NA';
    }

    return normalizedName;
  };
  // Icon mapping for KPI cards
  const kpiIcons = [
    <Co2 key="kpi-icon-0" sx={{ fontSize: 28 }} />,
    <Co2 key="kpi-icon-1" sx={{ fontSize: 28 }} />,
    <Co2 key="kpi-icon-2" sx={{ fontSize: 28 }} />,
    <Co2 key="kpi-icon-3" sx={{ fontSize: 28 }} />,
    <Co2 key="kpi-icon-4" sx={{ fontSize: 28 }} />,
    <Co2 key="kpi-icon-5" sx={{ fontSize: 28 }} />,
  ];

  const kpiColors = [
    CHART_COLORS.primary,
    CHART_COLORS.success,
    CHART_COLORS.info,
    CHART_COLORS.warning,
    CHART_COLORS.secondary,
    CHART_COLORS.danger,
  ];

  // Renders one sub-header summary card. Extracted from three near-identical
  // cards that differ only by accent color, icon, title, data and label format.
  const renderSubHeaderCard = (
    accent: string,
    icon: React.ReactNode,
    title: string,
    items: Array<{ label: string; value: string }>,
    formatLabel: (label: string) => React.ReactNode,
  ) => (
    <Grid item xs={12} md={isSplitView ? 12 : 4}>
      <Card
        elevation={0}
        sx={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px) saturate(180%)',
          border: `1px solid ${alpha(accent, 0.15)}`,
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
          '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: `0 16px 40px ${alpha(accent, 0.2)}`,
            border: `1px solid ${alpha(accent, 0.3)}`,
            background: 'rgba(255, 255, 255, 0.95)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${accent}, ${alpha(accent, 0.5)}, ${accent})`,
            backgroundSize: '200% 100%',
            animation: 'shimmer 3s ease-in-out infinite',
          },
          '@keyframes shimmer': {
            '0%, 100%': { backgroundPosition: '200% 0' },
            '50%': { backgroundPosition: '-200% 0' },
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${alpha(accent, 0.1)}, ${alpha(accent, 0.05)})`,
                color: accent,
              }}
            >
              {icon}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {title}
            </Typography>
          </Box>
          <Box>
            {items.slice(0, 3).map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.5,
                  p: 2,
                  borderRadius: 2,
                  background: alpha(accent, 0.03),
                  border: `1px solid ${alpha(accent, 0.1)}`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: alpha(accent, 0.08),
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Typography variant="body2" fontWeight={500}>{formatLabel(item.label)}</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ color: accent }}>
                  {formatNumber(Number.parseFloat(item.value.replaceAll(',', '')), 0)} kg
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box sx={{
      maxHeight: '100vh',
      // overflow: 'auto',
      background: 'linear-gradient(135deg, #fafbfc 0%, #f4f6f8 40%, #e8f4f2 100%)',
      position: 'relative',
      '@media print': {
        maxHeight: 'none',
        background: 'white',
        overflow: 'visible',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 50%, rgba(144, 191, 69, 0.04) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(108, 118, 124, 0.03) 0%, transparent 50%)',
        pointerEvents: 'none',
      },
    }}>
      <Box sx={{ p: { xs: 2, md: 4 }, position: 'relative', zIndex: 1 }}>
        {/* KPI Cards */}
        {kpiLoading ? (
          <Box
            sx={{
              mb: 4,
              display: isSplitView ? 'flex' : 'grid',
              flexWrap: 'wrap',
              gap: 3,
              width: '100%',
              gridTemplateColumns: isSplitView ? 'none' : 'repeat(4, 1fr)',
            }}
          >
            {['kpi-skeleton-0', 'kpi-skeleton-1', 'kpi-skeleton-2', 'kpi-skeleton-3'].map((key, idx) => (
              <KpiSkeletonCard key={key} idx={idx} isSplitView={isSplitView} />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              mb: 4,
              overflowX: 'visible',
              pb: isSplitView ? 2 : 0,
              '&::-webkit-scrollbar': {
                height: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: alpha(theme.palette.divider, 0.1),
                borderRadius: '10px',
              },
            }}
          >
            <Box
              sx={{
                display: isSplitView ? 'flex' : 'grid',
                flexWrap: 'wrap',
                gap: 3,
                width: '100%',
                gridTemplateColumns: isSplitView
                  ? 'none'
                  : 'repeat(4, 1fr)',
              }}
            >
              {kpiData.map((kpi, index) => (
                <Box
                  key={kpi.label}
                  sx={{
                    minWidth: isSplitView ? 180 : 'auto',
                    flex: isSplitView ? '1 1 250px' : '1 1 auto',
                  }}
                >
                  <ProfessionalKPICard
                    label={kpi.label}
                    subLabel={kpi.subLabel}
                    value={formatStringValue(kpi.value)}
                    unit={kpi.unit}
                    icon={kpiIcons[index % kpiIcons.length]}
                    color={kpiColors[index % kpiColors.length]}
                    loading={kpiLoading}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Sub Header - Transport Modes & Countries */}
        {(() => {
          if (subHeaderLoading) {
            return (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[0, 1, 2].map((i) => (
              <Grid item xs={12} md={isSplitView ? 12 : 4} key={`subheader-skeleton-${i}`}>
                <Paper sx={{ p: 2.5 }}>
                  <Skeleton variant="text" width="60%" sx={{ fontSize: '0.875rem', mb: 1.5 }} />
                  {['r0', 'r1', 'r2', 'r3', 'r4'].map((rowKey, idx) => (
                    <Box
                      key={`${i}-${rowKey}`}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 0.75,
                        borderTop: idx > 0 ? `1px solid ${alpha(theme.palette.divider, 0.06)}` : 'none',
                      }}
                    >
                      <Skeleton variant="text" width={`${30 + ((idx * 11) % 30)}%`} />
                      <Skeleton variant="text" width={70} />
                    </Box>
                  ))}
                </Paper>
              </Grid>
            ))}
          </Grid>
            );
          }
          if (subHeaderData) {
            return (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {renderSubHeaderCard(
              CHART_COLORS.info,
              <ShowChart />,
              'Top Transport Modes',
              subHeaderData.transportModes,
              (label) => formatTransportMode(label),
            )}
            {renderSubHeaderCard(
              CHART_COLORS.success,
              <Public />,
              'Top Origin Countries',
              subHeaderData.topOriginCountries,
              (label) => alpha3ToAlpha2Code(label),
            )}
            {renderSubHeaderCard(
              CHART_COLORS.warning,
              <FlightTakeoff />,
              'Top Destination Countries',
              subHeaderData.topDestinationCountries,
              (label) => alpha3ToAlpha2Code(label),
            )}
          </Grid>
            );
          }
          return null;
        })()}

        {/* Emission Chart - Monthly Transactions Bar Chart */}
        {(() => {
          if (emissionChartLoading) {
            return (
          <Card elevation={0} sx={{ mb: 4, borderRadius: 3, border: `1px solid ${alpha(CHART_COLORS.primary, 0.15)}` }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%', justifyContent: 'center' }}>
                    {[0, 1].map((i) => (
                      <Paper key={i} sx={{ p: 3 }}>
                        <Skeleton variant="text" width="80%" sx={{ fontSize: '0.875rem', mb: 1 }} />
                        <Skeleton variant="text" width="60%" sx={{ fontSize: '1.5rem' }} />
                      </Paper>
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Skeleton variant="text" width={240} sx={{ fontSize: '1rem', mb: 2 }} />
                  <Skeleton variant="rounded" width="100%" height={320} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
            );
          }
          if (emissionChartData) {
            return (
          <Card
            elevation={0}
            sx={{
              mb: 4,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px) saturate(180%)',
              border: `1px solid ${alpha(CHART_COLORS.primary, 0.15)}`,
              borderRadius: 3,
              overflow: 'hidden',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: `0 8px 32px ${alpha(CHART_COLORS.primary, 0.1)}`,
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Left side - Sub Headers */}
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%', justifyContent: 'center' }}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(CHART_COLORS.primary, 0.08)}, ${alpha(CHART_COLORS.primary, 0.02)})`,
                        border: `1px solid ${alpha(CHART_COLORS.primary, 0.15)}`,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                        {emissionChartData.primarySubHeader.name}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: CHART_COLORS.primary }}>
                        {emissionChartData.primarySubHeader.value}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(CHART_COLORS.info, 0.08)}, ${alpha(CHART_COLORS.info, 0.02)})`,
                        border: `1px solid ${alpha(CHART_COLORS.info, 0.15)}`,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                        {emissionChartData.secondarySubHeader.name}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: CHART_COLORS.info }}>
                        {formatNumber(emissionChartData.secondarySubHeader.value)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                {/* Right side - Bar Chart */}
                <Grid item xs={12} md={9}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${alpha(CHART_COLORS.primary, 0.1)}, ${alpha(CHART_COLORS.primary, 0.05)})`,
                        color: CHART_COLORS.primary,
                      }}
                    >
                      <BarChartIcon />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {emissionChartData.chartData.name}
                    </Typography>
                  </Box>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={emissionChartData.chartData.data} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.06)} />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 12, fill: '#6C757D' }}
                        tickLine={false}
                        axisLine={{ stroke: alpha('#000', 0.1) }}
                      />
                      <YAxis
                        tick={{ fontSize: 12, fill: '#6C757D' }}
                        tickLine={false}
                        axisLine={{ stroke: alpha('#000', 0.1) }}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: 8,
                          border: `1px solid ${alpha(CHART_COLORS.primary, 0.2)}`,
                          boxShadow: `0 4px 12px ${alpha('#000', 0.1)}`,
                        }}
                      />
                      <Bar
                        dataKey="value"
                        fill={CHART_COLORS.primary}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={50}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
            );
          }
          return null;
        })()}

        {/* Tabs and Table */}
        <Card
          elevation={0}
          sx={{
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px) saturate(180%)',
            border: `1px solid ${alpha(CHART_COLORS.primary, 0.12)}`,
            borderRadius: 3,
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: `0 8px 32px ${alpha(CHART_COLORS.primary, 0.1)}`,
            },
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {/* Tabs Header */}
            <Box sx={{
              borderBottom: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
              mb: 3,
            }}>
              <Tabs
                value={tabs.findIndex((t) => t.id === selectedTab?.id)}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    minHeight: 48,
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      color: CHART_COLORS.primary,
                      fontWeight: 600,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: CHART_COLORS.primary,
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                  },
                }}
              >
                {tabs.map((tab) => (
                  <Tab key={tab.id} label={tab?.name === 'Customer' ? 'Sub-Customer' : tab?.name} />
                ))}
              </Tabs>
            </Box>

            {/* Table Controls */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                flexWrap: 'wrap',
                gap: 2,
                p: 2,
                borderRadius: 2,
                background: alpha(CHART_COLORS.primary, 0.03),
                border: `1px solid ${alpha(CHART_COLORS.primary, 0.1)}`,
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 150,
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: CHART_COLORS.primary,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: CHART_COLORS.primary,
                      },
                    },
                  }}
                >
                  <InputLabel>Emission Type</InputLabel>
                  <Select
                    value={selectedEmission}
                    label="Emission Type"
                    onChange={(e) => setSelectedEmission(e.target.value)}
                  >
                    <MenuItem value="TOT">TOT CO2e</MenuItem>
                    <MenuItem value="OPS">OPS CO2e</MenuItem>
                    <MenuItem value="ENE">ENE CO2e</MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    minWidth: 120,
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: CHART_COLORS.primary,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: CHART_COLORS.primary,
                      },
                    },
                  }}
                >
                  <InputLabel>Weight Unit</InputLabel>
                  <Select
                    value={selectedWeight}
                    label="Weight Unit"
                    onChange={(e) => setSelectedWeight(e.target.value)}
                  >
                    <MenuItem value="kg">Kilogram (kg)</MenuItem>
                    <MenuItem value="ton">Metric Ton (t)</MenuItem>
                    <MenuItem value="lb">Pound (lb)</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                size="small"
                sx={{
                  borderColor: CHART_COLORS.primary,
                  color: CHART_COLORS.primary,
                  '&:hover': {
                    borderColor: CHART_COLORS.primary,
                    background: alpha(CHART_COLORS.primary, 0.1),
                  },
                }}
              >
                Export
              </Button> */}
            </Box>

            {/* Data Table */}
            {(() => {
              if (tabDataLoading) {
                return (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: 2.5,
                  border: `1px solid ${alpha(CHART_COLORS.primary, 0.1)}`,
                  overflow: 'hidden',
                  background: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(10px)',
                  mb: 3,
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: `linear-gradient(135deg, ${alpha(CHART_COLORS.primary, 0.08)}, ${alpha(CHART_COLORS.primary, 0.04)})` }}>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'center', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 2 }}>
                        {selectedTab?.name === 'Customer' ? 'Sub-Customer' : selectedTab?.name || 'Dimension'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'center', fontSize: '0.75rem', letterSpacing: '0.5px' }}>WEIGHT ({getWeightUnit()})</TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'center', fontSize: '0.75rem', letterSpacing: '0.5px' }}>EI (GR/TKM)</TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'center', fontSize: '0.75rem', letterSpacing: '0.5px' }}>EMISSIONS KG CO2e</TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'center', fontSize: '0.75rem', letterSpacing: '0.5px' }}>NOT COMPENSATED EMISSIONS KG CO2e</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {['s0', 's1', 's2', 's3', 's4', 's5', 's6', 's7'].map((skeletonKey, idx) => (
                      <TableRow key={skeletonKey}>
                        <TableCell align="center"><Skeleton variant="text" width={`${50 + ((idx * 9) % 30)}%`} sx={{ mx: 'auto' }} /></TableCell>
                        <TableCell align="center"><Skeleton variant="text" width={80} sx={{ mx: 'auto' }} /></TableCell>
                        <TableCell align="center"><Skeleton variant="text" width={70} sx={{ mx: 'auto' }} /></TableCell>
                        <TableCell align="center"><Skeleton variant="text" width={90} sx={{ mx: 'auto' }} /></TableCell>
                        <TableCell align="center"><Skeleton variant="text" width={90} sx={{ mx: 'auto' }} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
                );
              }
              if (tabData) {
                return (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: 2.5,
                  border: `1px solid ${alpha(CHART_COLORS.primary, 0.1)}`,
                  overflow: 'hidden',
                  background: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(10px)',
                  mb: 3,
                  '@media print': {
                    overflow: 'visible',
                    backdropFilter: 'none',
                  },
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        background: `linear-gradient(135deg, ${alpha(CHART_COLORS.primary, 0.08)}, ${alpha(CHART_COLORS.primary, 0.04)})`,
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          textAlign: 'center',
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: 'text.primary',
                          py: 2,
                        }}
                      >
                        {selectedTab?.name === 'Customer' ? 'Sub-Customer' : selectedTab?.name || 'Dimension'}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          color: 'text.primary',
                        }}
                      >
                        WEIGHT ({getWeightUnit()})
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          color: 'text.primary',
                        }}
                      >
                        EI (GR/TKM)
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textAlign: 'center',
                          textTransform: 'none !important',
                          letterSpacing: '0.5px',
                          color: 'text.primary',
                        }}
                      >
                        EMISSIONS KG CO2e
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textAlign: 'center',
                          textTransform: 'none !important',
                          letterSpacing: '0.5px',
                          color: 'text.primary',
                        }}
                      >
                        NOT COMPENSATED EMISSIONS KG CO2e
                      </TableCell>
                      {/* Distance column hidden from UI */}
                      <TableCell
                        sx={{
                          display: 'none',
                        }}
                      >
                        Distance (km)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Total Row */}
                    {tabData.metadata?.total && (
                      <TableRow
                        sx={{
                          background: `linear-gradient(135deg, ${alpha(CHART_COLORS.primary, 0.15)}, ${alpha(CHART_COLORS.primary, 0.08)})`,
                          '& td': {
                            borderBottom: `2px solid ${alpha(CHART_COLORS.primary, 0.2)}`,
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            textAlign: 'center',
                            fontSize: '0.9rem',
                            color: CHART_COLORS.primary,
                          }}
                        >
                          TOTAL
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            textAlign: 'center',
                            color: CHART_COLORS.primary,
                          }}
                        >
                          {convertWeight(tabData.metadata.total.transported_weight)}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            textAlign: 'center',
                            color: CHART_COLORS.primary,
                          }}
                        >
                          {formatNumber(tabData.metadata.total.emission_intensity, 4)}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            textAlign: 'center',
                            color: CHART_COLORS.primary,
                          }}
                        >
                          {formatNumber(tabData.metadata.total.total_emissions, 2)}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            textAlign: 'center',
                            color: CHART_COLORS.primary,
                          }}
                        >
                          {formatNumber(tabData.metadata.total.not_compensated_emissions, 2)}
                        </TableCell>
                        {/* Distance column hidden from UI */}
                        <TableCell
                          sx={{ display: 'none' }}
                        >
                          {formatNumber(tabData.metadata.total.distance, 2)}
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Data Rows */}
                    {tabData.data.map((row) => (
                      <TableRow
                        key={row.dimension}
                        sx={{
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            background: `linear-gradient(90deg, ${alpha(CHART_COLORS.primary, 0.08)}, ${alpha(CHART_COLORS.primary, 0.04)}, ${alpha(CHART_COLORS.primary, 0.08)})`,
                            transform: 'scale(1.005)',
                            boxShadow: `0 2px 8px ${alpha(CHART_COLORS.primary, 0.1)}`,
                          },
                          '& td': {
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                          },
                        }}
                      >
                        <TableCell align="center" sx={{ fontWeight: 500 }}>
                          {selectedTab?.name === 'Customer'
                            ? convertSubCustomerName(row.dimension)
                            : formatTransportMode(row.dimension)}
                        </TableCell>
                        <TableCell align="center">{convertWeight(row.transported_weight)}</TableCell>
                        <TableCell align="center">{formatNumber(row.emission_intensity, 4)}{typeof row.dimension === 'string' && ['hub', 'transshipment'].includes(row.dimension.toLowerCase().trim()) ? ' gr/T' : ''}</TableCell>
                        <TableCell align="center">{formatNumber(row.total_emissions, 2)}</TableCell>
                        <TableCell align="center">{formatNumber(row.not_compensated_emissions, 2)}</TableCell>
                        {/* Distance column hidden from UI */}
                        <TableCell align="center" sx={{ display: 'none' }}>{formatNumber(row.distance, 2)}</TableCell>
                      </TableRow>
                    ))}

                    {tabData.data.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <TableChartIcon
                              sx={{
                                fontSize: 64,
                                color: alpha(theme.palette.text.secondary, 0.3),
                              }}
                            />
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              No data available for the selected filters
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                {isPaginatedTab && (
                  <TablePagination
                    component="div"
                    count={tabData.metadata?.totalRows ?? tabData.metadata?.total_rows ?? 0}
                    page={tabPage}
                    onPageChange={(_event, newPage) => setTabPage(newPage)}
                    rowsPerPage={TAB_PAGE_SIZE}
                    rowsPerPageOptions={[TAB_PAGE_SIZE]}
                    onRowsPerPageChange={() => { /* fixed page size */ }}
                  />
                )}
              </TableContainer>
                );
              }
              return null;
            })()}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Dashboard;
